import React, { useState, useEffect, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import SendIcon from '@mui/icons-material/Send';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { useGet, usePut } from '../../../sharedModules/hooks/useQuery';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import axios from '../../../sharedModules/utils/axios.utils';

type FieldType = 'condition' | 'yesno' | 'hvac_age' | 'plumbing_age';
type SectionKey = 'hvac' | 'plumbing' | 'electrical' | 'water_treatment';

interface AlertOption {
  value: string;
  label: string;
  color: string;
}

const FIELD_OPTIONS: Record<FieldType, AlertOption[]> = {
  condition: [
    { value: 'needs_attention', label: 'Needs Attention', color: '#ff9800' },
    { value: 'requires_service', label: 'Requires Service', color: '#f44336' },
  ],
  yesno: [
    { value: 'yes', label: 'Yes', color: '#9e9e9e' },
    { value: 'no', label: 'No', color: '#9e9e9e' },
  ],
  hvac_age: [
    { value: '1_10', label: '1-10 Years', color: '#4caf50' },
    { value: '11_15', label: '11-15 Years', color: '#ff9800' },
    { value: '15_plus', label: '15+ Years', color: '#f44336' },
  ],
  plumbing_age: [
    { value: '1_6', label: '1-6 Years', color: '#4caf50' },
    { value: '6_9', label: '6-9 Years', color: '#ff9800' },
    { value: '10_15', label: '10-15 Years', color: '#f44336' },
  ],
};

interface AttributeConfig {
  key: string;
  label: string;
  type: FieldType;
}

interface SectionConfig {
  title: string;
  sectionKey: SectionKey;
  attributes: AttributeConfig[];
}

const SECTIONS: SectionConfig[] = [
  {
    title: 'HVAC',
    sectionKey: 'hvac',
    attributes: [
      { key: 'hvac_age', label: 'Age', type: 'hvac_age' },
      { key: 'hvac_overall_visual_condition', label: 'Overall / Visual Condition', type: 'condition' },
      { key: 'hvac_overall_operation', label: 'Overall Operation', type: 'condition' },
      { key: 'hvac_air_filter_condition', label: 'Air Filter Condition', type: 'condition' },
      { key: 'hvac_indoor_air_quality_condition', label: 'Indoor Air Quality Device', type: 'condition' },
      { key: 'hvac_offered_maintenance_plan', label: 'Offered Maintenance Plan', type: 'yesno' },
      { key: 'hvac_proper_clearance', label: 'Proper Clearance', type: 'yesno' },
      { key: 'hvac_service_record_updated', label: 'Service Record Updated', type: 'yesno' },
    ],
  },
  {
    title: 'Plumbing',
    sectionKey: 'plumbing',
    attributes: [
      { key: 'plumbing_age', label: 'Age', type: 'plumbing_age' },
      { key: 'plumbing_overall_visual_condition', label: 'Overall / Visual Condition', type: 'condition' },
      { key: 'plumbing_expansion_tank_condition', label: 'Expansion Tank', type: 'condition' },
      { key: 'plumbing_visible_leaks_or_rust', label: 'Visible Leaks or Rust', type: 'yesno' },
      { key: 'plumbing_proper_clearance', label: 'Proper Clearance', type: 'yesno' },
      { key: 'plumbing_recommended_annual_maintenance', label: 'Recommended Annual Maintenance', type: 'yesno' },
      { key: 'plumbing_installed_coupon', label: 'Installed Coupon', type: 'yesno' },
    ],
  },
  {
    title: 'Electrical',
    sectionKey: 'electrical',
    attributes: [
      { key: 'electrical_overall_visual_condition', label: 'Overall / Visual Condition', type: 'condition' },
      { key: 'electrical_whole_house_surge_protection', label: 'Whole House Surge Protection', type: 'yesno' },
      {
        key: 'electrical_wiring_secured_properly_terminated',
        label: 'Wiring Secured / Properly Terminated',
        type: 'yesno',
      },
      { key: 'electrical_whole_home_generator_condition', label: 'Whole Home Generator', type: 'condition' },
      { key: 'electrical_proper_clearance', label: 'Proper Clearance', type: 'yesno' },
    ],
  },
  {
    title: 'Water Treatment',
    sectionKey: 'water_treatment',
    attributes: [
      { key: 'water_treatment_visible_condition', label: 'Visible Condition of Equipment', type: 'condition' },
      { key: 'water_treatment_recommended_annual_maintenance', label: 'Recommended Annual Maintenance', type: 'yesno' },
      { key: 'water_treatment_filters_or_salt_required', label: 'Filters or Salt Required', type: 'yesno' },
      { key: 'water_treatment_water_quality_complaints', label: 'Water Quality Complaints', type: 'yesno' },
      { key: 'water_treatment_location_for_solution', label: 'Location for Solution', type: 'yesno' },
    ],
  },
];

const ALL_KEYS = SECTIONS.flatMap((s) => s.attributes.map((a) => a.key));

type AlertSettings = Record<string, string[]>;

interface SectionRecipients {
  admin_user_ids: number[];
  salesperson_user_ids: number[];
  notify_salespeople: boolean;
}

type RecipientsState = Record<SectionKey, SectionRecipients>;

const DEFAULT_SECTION_RECIPIENTS: SectionRecipients = {
  admin_user_ids: [],
  salesperson_user_ids: [],
  notify_salespeople: false,
};

const DEFAULT_RECIPIENTS: RecipientsState = {
  hvac: { ...DEFAULT_SECTION_RECIPIENTS },
  plumbing: { ...DEFAULT_SECTION_RECIPIENTS },
  electrical: { ...DEFAULT_SECTION_RECIPIENTS },
  water_treatment: { ...DEFAULT_SECTION_RECIPIENTS },
};

const DEFAULT_SETTINGS: AlertSettings = Object.fromEntries(ALL_KEYS.map((k) => [k, []]));

interface UserOption {
  value: number;
  label: string;
}

interface AlertSettingsResponse {
  result: {
    settings?: AlertSettings;
    recipients?: Record<string, any>;
  };
  admin_user_options?: UserOption[];
  salesperson_user_options?: UserOption[];
}

const sectionHeaderSx = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
  bgcolor: '#3d5a80',
  px: 2,
  py: 1,
  borderRadius: '4px 4px 0 0',
};

const subHeaderSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1,
  mt: 2,
};

const getOptionLabel = (value: string, fieldType: FieldType): string => {
  const opt = FIELD_OPTIONS[fieldType]?.find((o) => o.value === value);
  return opt?.label || value;
};

const getOptionColor = (value: string, fieldType: FieldType): string => {
  const opt = FIELD_OPTIONS[fieldType]?.find((o) => o.value === value);
  return opt?.color || '#999';
};

interface SectionRecipientsPanelProps {
  sectionKey: SectionKey;
  recipients: SectionRecipients;
  adminUserOptions: UserOption[];
  salespersonUserOptions: UserOption[];
  onChange: (sectionKey: SectionKey, updated: SectionRecipients) => void;
}

const SectionRecipientsPanel: React.FC<SectionRecipientsPanelProps> = ({
  sectionKey,
  recipients,
  adminUserOptions,
  salespersonUserOptions,
  onChange,
}) => {
  const updateField = (field: keyof SectionRecipients, value: any) => {
    onChange(sectionKey, { ...recipients, [field]: value });
  };

  const handleAdminUsersChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    updateField('admin_user_ids', typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const handleSalespersonUsersChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    updateField('salesperson_user_ids', typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const renderUserSelect = (
    label: string,
    value: number[],
    options: UserOption[],
    emptyMessage: string,
    handleChange: (e: SelectChangeEvent<number[]>) => void,
  ) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        label={label}
        onChange={handleChange}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as number[]).map((id) => {
              const user = options.find((u) => u.value === id);
              return <Chip key={id} label={user?.label ?? id} size="small" />;
            })}
          </Box>
        )}
      >
        {options.length === 0 ? (
          <MenuItem disabled>{emptyMessage}</MenuItem>
        ) : (
          options.map((user) => (
            <MenuItem key={user.value} value={user.value}>
              <Checkbox size="small" checked={value.includes(user.value)} />
              <ListItemText primary={user.label} />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );

  return (
    <>
      <Typography sx={subHeaderSx}>Admin Recipients</Typography>
      <Box sx={{ px: 1, mb: 1 }}>
        {renderUserSelect(
          'Admin Users',
          recipients.admin_user_ids,
          adminUserOptions,
          'No admin users found.',
          handleAdminUsersChange,
        )}
      </Box>

      <Typography sx={subHeaderSx}>Salesperson Notifications</Typography>
      <Box sx={{ px: 1, mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={recipients.notify_salespeople}
              onChange={(e) => updateField('notify_salespeople', e.target.checked)}
            />
          }
          label={<Typography variant="body2">Notify Salespeople</Typography>}
        />
        {recipients.notify_salespeople && (
          <Box sx={{ mt: 1 }}>
            {renderUserSelect(
              'Salespeople',
              recipients.salesperson_user_ids,
              salespersonUserOptions,
              'No salesperson users found.',
              handleSalespersonUsersChange,
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

const AlertNotificationPage: React.FC = () => {
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);
  const [settings, setSettings] = useState<AlertSettings>(DEFAULT_SETTINGS);
  const [recipients, setRecipients] = useState<RecipientsState>(DEFAULT_RECIPIENTS);
  const [adminUserOptions, setAdminUserOptions] = useState<UserOption[]>([]);
  const [salespersonUserOptions, setSalespersonUserOptions] = useState<UserOption[]>([]);
  const [previewEmail, setPreviewEmail] = useState('');
  const [sendingPreview, setSendingPreview] = useState(false);

  const {
    data: response,
    isLoading,
    error,
  } = useGet<AlertSettingsResponse>({
    url: '/home_services/inspection_alert_notification_settings',
  });

  const { mutateAsync, isPending } = usePut();

  useEffect(() => {
    const data = response?.data;
    if (!data) return;

    const result = data.result;
    if (result) {
      const saved = result.settings || {};
      const merged: AlertSettings = {};
      ALL_KEYS.forEach((key) => {
        const val = saved[key];
        merged[key] = Array.isArray(val) ? val : val ? [val as unknown as string] : [];
      });
      setSettings(merged);

      const savedRecipients = result.recipients || {};
      const mergedRecipients: RecipientsState = { ...DEFAULT_RECIPIENTS };
      (Object.keys(DEFAULT_RECIPIENTS) as SectionKey[]).forEach((sk) => {
        const sectionData = savedRecipients[sk] || {};
        mergedRecipients[sk] = {
          admin_user_ids: Array.isArray(sectionData.admin_user_ids) ? sectionData.admin_user_ids.map(Number) : [],
          salesperson_user_ids: Array.isArray(sectionData.salesperson_user_ids)
            ? sectionData.salesperson_user_ids.map(Number)
            : [],
          notify_salespeople: Boolean(sectionData.notify_salespeople),
        };
      });
      setRecipients(mergedRecipients);
    }

    if (data.admin_user_options) setAdminUserOptions(data.admin_user_options);
    if (data.salesperson_user_options) setSalespersonUserOptions(data.salesperson_user_options);
  }, [response]);

  const handleChange = (key: string, event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      [key]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleRecipientsChange = (sectionKey: SectionKey, updated: SectionRecipients) => {
    setRecipients((prev) => ({ ...prev, [sectionKey]: updated }));
  };

  const handleSave = async () => {
    try {
      await mutateAsync({
        url: '/home_services/inspection_alert_notification_settings',
        data: {
          inspection_alert_notification_settings: settings,
          recipients,
        },
      });
      showSuccessSnackBar?.('Alert settings saved.');
    } catch {
      showErrorSnackBar?.('Failed to save alert settings.');
    }
  };

  const handleSendPreview = async () => {
    if (!previewEmail) return;
    setSendingPreview(true);
    try {
      await axios.post('/home_services/inspection_alert_notification_settings/preview', { email: previewEmail });
      showSuccessSnackBar?.(`Preview alert sent to ${previewEmail}`);
    } catch {
      showErrorSnackBar?.('Failed to send preview alert email.');
    } finally {
      setSendingPreview(false);
    }
  };

  const loading = isLoading || isPending;
  const errorMessage = error ? 'Failed to load alert settings.' : '';

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={3} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader mainTitle="Home Services" subTitle="Alert Notifications" />
        </Grid>

        {errorMessage && (
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Configure which inspection report values trigger an alert email, and who should receive those alerts per
            section.
          </Typography>
        </Grid>

        {SECTIONS.map((section) => (
          <Grid item xs={12} md={6} key={section.title}>
            <Paper style={{ overflow: 'hidden' }}>
              <Typography sx={sectionHeaderSx}>{section.title}</Typography>
              <Grid container spacing={2} style={{ padding: 16 }}>
                <Grid item xs={12}>
                  <SectionRecipientsPanel
                    sectionKey={section.sectionKey}
                    recipients={recipients[section.sectionKey]}
                    adminUserOptions={adminUserOptions}
                    salespersonUserOptions={salespersonUserOptions}
                    onChange={handleRecipientsChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline" color="textSecondary" display="block" sx={{ lineHeight: 1.5 }}>
                    Alert Triggers
                  </Typography>
                </Grid>
                {section.attributes.map(({ key, label, type }) => {
                  const options = FIELD_OPTIONS[type];
                  const selected = settings[key] || [];

                  return (
                    <Grid item xs={12} key={key}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{label}</InputLabel>
                        <Select
                          multiple
                          value={selected}
                          label={label}
                          onChange={(e) => handleChange(key, e)}
                          renderValue={(sel) => {
                            const vals = sel as string[];
                            if (vals.length === 0) return 'No Alert';
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {vals.map((v) => (
                                  <Chip
                                    key={v}
                                    label={getOptionLabel(v, type)}
                                    size="small"
                                    sx={{
                                      bgcolor: getOptionColor(v, type),
                                      color: '#fff',
                                      fontWeight: 600,
                                      fontSize: '11px',
                                    }}
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        >
                          {options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Checkbox checked={selected.includes(opt.value)} />
                              <ListItemText primary={opt.label} />
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: opt.color,
                                  ml: 1,
                                  flexShrink: 0,
                                }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
            Save Settings
          </Button>
        </Grid>

        {/* Send Preview */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        <Grid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Send Preview
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Send a sample alert email using the most recent inspection report to any email address.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Email Address"
                value={previewEmail}
                onChange={(e) => setPreviewEmail(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendPreview}
                disabled={sendingPreview || !previewEmail}
              >
                {sendingPreview ? 'Sending...' : 'Send Preview'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default AlertNotificationPage;
